import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ToolFormat } from '.';
import TallyConnect from '@src/assets/images/tally-connect.svg';
import LabeledTextField from '@src/components/CustomTextField/LabeledTextField';
import { CircularProgress } from '@mui/material';
import CustomButton from '@src/components/CustomButton/CustomButton';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import { error } from 'console';
import { useAppSelector } from '@src/store/hooks';
import CustomModal from '@src/components/CustomModal';
import SecurityAlertModal from '@src/components/ConfirmModal/SecurityAlertModal';
import { CustomAlertType } from '@src/interfaces/type';

const StyledContainer = styled.div`
	display: flex;
	max-width: 100%;
	height: 100%;
	background-color: var(--chat-header-bg);
	letter-spacing: 0.28px;
	overflow: scroll;
	flex-grow: 1;
	@media screen and (max-width: 600px) {
		height: calc(100vh - 166px);
	}
`;

const Container = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	height: 100%;
	background-color: var(--chat-header-bg);
	padding-top: 34px;
	padding-bottom: 34px;
	gap: 12px;
	@media screen and (max-width: 900px) {
		padding: 34px;
	}
	#image-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		gap: 8px;
		img {
			width: 186px;
			height: 91px;
		}
		h3 {
			color: var(--default-text-color);
			font-size: 14px;
			font-weight: 600;
		}
		a {
			color: var(--ex-2, #5e94ff);
			font-size: 14px;
			font-weight: 600;
			text-decoration-line: underline;
			cursor: pointer;
		}
	}
	#info-container {
		display: flex;
		flex-direction: column;
		gap: 8px;
		max-width: 619px;
		@media screen and (max-width: 900px) {
			max-width: 100%;
		}
		p {
			color: var(--default-text-color);
			font-size: 14px;
			font-weight: 600;
			letter-spacing: 0.28px;
		}
		ul {
			display: flex;
			flex-direction: column;
			gap: 6px;
		}
		.points {
			margin: 0;
			text-align: left;
			list-style-type: disc;
			color: var(--color-font-grey);
			font-size: 13px;
			font-weight: 600;
			letter-spacing: 0.26px;
		}
	}
	#fields-container {
		display: flex;
		flex-direction: column;
		gap: 18px;
		@media screen and (max-width: 900px) {
			max-width: 100%;
		}
		h3 {
			color: var(--Black, #1a1a1a);
			font-size: 18px;
			font-weight: 700;
			line-height: 24px;
			width: 619px;
		}
		#credentials {
			display: flex;
			align-items: center;
			gap: 7px;
		}
	}
`;

interface ManageTallyProps {
	data: ToolFormat;
	setData: (data: ToolFormat) => void;
	setAlert: (data: CustomAlertType) => void;
	setApiCall: (data: boolean) => void;
	displayCustomError: any;
}
const ManageTally: React.FC<ManageTallyProps> = ({
	data,
	setData,
	setAlert,
	setApiCall,
	displayCustomError,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const importantPoints = [
		'Users can add their credentials.',
		'The addon can be downloaded by clicking on the provided download link.',
		'After downloading, users can paste the addon on their system.',
	];
	const [confirmDialog, setConfirmDialog] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [tallyCredentials, setTallyCredentials] = useState({
		supplier: '',
		username: '',
		password: '',
	});

	//Mobile Screen States
	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	const updateWindowDimensions = () => {
		setWindowDimensions({
			width: window.innerWidth,
			height: window.innerHeight,
		});
	};

	const createCredentials = () => {
		setLoading(true);
		apiService.generateTallyCredentials(
			(response: AxiosResponse) => {
				const credentials = response?.data?.data;
				setData({
					userName: credentials?.username,
					password: credentials?.password,
					tallyIntegrated: true,
				});
				setShowPassword(true);
				setLoading(false);
				setConfirmDialog(false);
				setApiCall(true);
			},
			(error: AxiosError) => {
				setLoading(false);
				displayCustomError('Something went wrong!');
			}
		);
	};

	const generateCredentials = () => {
		if (!data?.userName) {
			createCredentials();
		} else {
			//revalidate case
			setConfirmDialog(true);
		}
	};

	const passwordFieldValue = () => {
		if (!data?.tallyIntegrated) {
			return true;
		}
		return showPassword;
	};

	//For Mobile Screen
	useEffect(() => {
		window.addEventListener('resize', updateWindowDimensions);

		return () => {
			window.removeEventListener('resize', updateWindowDimensions);
		};
	}, []);

	useEffect(() => {
		updateWindowDimensions();
	}, []);

	return (
		<StyledContainer>
			<Container>
				<div id="image-container">
					{/* @ts-ignore */}
					<img src={TallyConnect} alt="tally-image" />
					<h3>Connect Tally to your Cheeni account</h3>
					<a>Click here to download extension</a>
				</div>
				<div id="info-container">
					<p>
						To link your accounts, please refer to the note provided, guiding you through the
						process of entering your credentials into Cheeni's Tally add-on.
					</p>
					<ul>
						{importantPoints?.map((point: string, index: number) => {
							return (
								<li key={index} className="points">
									{point}
								</li>
							);
						})}
					</ul>
				</div>
				<div id="fields-container">
					<h3>Generate Credentials</h3>
					<LabeledTextField
						label="Supplier"
						value={currentUser?.organization?.name}
						styles={
							windowDimensions?.width > 900
								? { width: '569px', height: '36px' }
								: { width: '95%', height: '36px' }
						}
						onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {}}
						placeholder=""
						readOnly
					/>
					{windowDimensions?.width > 900 ? (
						<div id="credentials" style={{ gap: data?.userName ? '7px' : '15px' }}>
							<LabeledTextField
								label="Username"
								value={data?.userName}
								styles={{ width: '277px', height: '36px' }}
								onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {}}
								placeholder="Daniel Hamilton"
								readOnly
								allowCopy
							/>
							<LabeledTextField
								label="Password"
								value={data?.password}
								styles={{ width: '277px', height: '36px' }}
								onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {}}
								placeholder="**************"
								readOnly
								type="password"
								allowCopy
								showPassword={passwordFieldValue()}
							/>
						</div>
					) : (
						<>
							<LabeledTextField
								label="Username"
								value={data?.userName}
								styles={{ width: '95%', height: '36px' }}
								onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {}}
								placeholder="Daniel Hamilton"
								readOnly
								allowCopy
							/>
							<LabeledTextField
								label="Password"
								value={data?.password}
								styles={{ width: '95%', height: '36px' }}
								onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {}}
								placeholder="**************"
								readOnly
								type="password"
								allowCopy
								showPassword={passwordFieldValue()}
							/>
						</>
					)}
					<div style={{ width: '619px', marginTop: '12px' }}>
						<CustomButton
							type="submit"
							title={
								loading ? (
									<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
								) : (
									'Generate Credentials'
								)
							}
							disabled={loading}
							handleClick={generateCredentials}
							styles={{
								width: '191px',
								height: '36px',
								borderRadius: '4px',
								background: 'var(--color-primary)',
								color: 'var(--default-text-color)',
							}}
						/>
					</div>
				</div>
			</Container>
			{confirmDialog ? (
				<CustomModal
					open={confirmDialog}
					setOpen={setConfirmDialog}
					header={false}
					style={{ width: '407px', height: 'auto' }}
					children={
						<SecurityAlertModal
							handleClose={() => setConfirmDialog(false)}
							handleConfirm={createCredentials}
							loading={loading}
						/>
					}
				/>
			) : null}
		</StyledContainer>
	);
};

export default ManageTally;
